import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Send, Loader2, CheckCircle, Search, User, Calculator, Check,
  Eye, Download, Mail, Copy as CopyIcon, Clock,
} from "lucide-react";

import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { formatAdminError } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVOICE_BCC = "academy@sccfootball.com";

const PRESET_DESCRIPTIONS = [
  "Registration fee (paid term fees but forgot registration)",
  "Missed session charge",
  "Tournament entry fee",
  "Kit / Uniform",
  "Late payment admin fee",
  "Guest / trial session",
  "Refund adjustment",
];

export default function AdminManualInvoice() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [parent, setParent] = useState(null);

  // Child selection — driven by dropdown from parent's linked kids
  const [parentChildren, setParentChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(""); // child id, "__none__", or "__manual__"
  const [manualChildName, setManualChildName] = useState("");

  // Optional registration linkage — when present, marking this manual invoice
  // as paid will also settle the linked registration if total paid >= total fee.
  const [eligibleRegs, setEligibleRegs] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [linkedRegistrationId, setLinkedRegistrationId] = useState("");

  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Fee calculator state
  const [showCalc, setShowCalc] = useState(false);
  const [terms, setTerms] = useState([]);
  const [calcTermId, setCalcTermId] = useState("");
  const [calcChildren, setCalcChildren] = useState(1);
  const [calcIncludeRegFee, setCalcIncludeRegFee] = useState(false);
  const [calcSessionsPerWeek, setCalcSessionsPerWeek] = useState(0);
  const [calcIncludeDM, setCalcIncludeDM] = useState(false);
  const [calcPerWeekOverride, setCalcPerWeekOverride] = useState("");
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  // Parent autocomplete
  useEffect(() => {
    if (parent || q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/academy/admin/parents-search`, {
          params: { q },
          withCredentials: true,
        });
        setResults(res.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, parent]);

  // When parent is selected, fetch their linked children
  useEffect(() => {
    if (!parent) {
      setParentChildren([]);
      setSelectedChildId("");
      setManualChildName("");
      setEligibleRegs([]);
      setLinkedRegistrationId("");
      return;
    }
    setLoadingChildren(true);
    axios
      .get(`${API}/academy/admin/parent/${parent.id}/children`, { withCredentials: true })
      .then((r) => {
        const kids = Array.isArray(r.data) ? r.data : (r.data?.children || []);
        setParentChildren(kids);
        // Default selection: first child if only one, otherwise "none"
        setSelectedChildId(kids.length === 1 ? kids[0].id : "__none__");
      })
      .catch(() => {
        setParentChildren([]);
        setSelectedChildId("__none__");
      })
      .finally(() => setLoadingChildren(false));

    // Fetch registrations the admin could link this manual invoice to.
    setLoadingRegs(true);
    setLinkedRegistrationId("");
    axios
      .get(`${API}/academy/admin/manual-invoice/eligible-registrations`, {
        params: { parent_id: parent.id },
        withCredentials: true,
      })
      .then((r) => setEligibleRegs(r.data?.results || []))
      .catch(() => setEligibleRegs([]))
      .finally(() => setLoadingRegs(false));
  }, [parent]);

  const resolveChild = () => {
    if (selectedChildId === "__manual__") {
      return { id: null, name: manualChildName.trim() || null };
    }
    if (selectedChildId === "__none__" || !selectedChildId) {
      return { id: null, name: null };
    }
    const c = parentChildren.find((k) => k.id === selectedChildId);
    return { id: selectedChildId, name: c?.name || null };
  };

  const resetForm = () => {
    setParent(null);
    setQ("");
    setParentChildren([]);
    setSelectedChildId("");
    setManualChildName("");
    setEligibleRegs([]);
    setLinkedRegistrationId("");
    setDescription("");
    setAmount("");
    setNote("");
    setSendEmail(true);
    setShowCalc(false);
    setCalcIncludeRegFee(false);
    setCalcSessionsPerWeek(0);
    setCalcIncludeDM(false);
    setCalcPerWeekOverride("");
    setCalcResult(null);
    setCalcChildren(1);
    setPreviewData(null);
  };

  // Load terms once (for the calculator dropdown)
  useEffect(() => {
    axios.get(`${API}/academy/terms`, { withCredentials: true })
      .then((r) => {
        const finalised = (r.data || []).filter((t) => t.is_finalized);
        setTerms(finalised);
        if (finalised.length && !calcTermId) setCalcTermId(finalised[0].id);
      })
      .catch(() => setTerms([]));
  }, []);

  // Recompute the calculator whenever any input changes
  useEffect(() => {
    if (!showCalc || !parent) {
      setCalcResult(null);
      return;
    }
    const nothingSelected = !calcIncludeRegFee && calcSessionsPerWeek <= 0 && !calcIncludeDM;
    if (nothingSelected) {
      setCalcResult(null);
      return;
    }
    const t = setTimeout(async () => {
      setCalcLoading(true);
      try {
        const res = await axios.post(
          `${API}/academy/admin/manual-invoice/calculate`,
          {
            parent_id: parent.id,
            term_id: calcTermId || null,
            children_count: calcChildren,
            include_registration_fee: calcIncludeRegFee,
            sessions_per_week: calcSessionsPerWeek,
            include_digital_membership: calcIncludeDM,
            per_week_price: calcPerWeekOverride ? parseFloat(calcPerWeekOverride) : null,
          },
          { withCredentials: true }
        );
        setCalcResult(res.data);
      } catch (err) {
        { const __e = formatAdminError(err, "Calc failed"); toast.error(__e.message, __e.opts); };
      } finally {
        setCalcLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [showCalc, parent, calcTermId, calcChildren, calcIncludeRegFee, calcSessionsPerWeek, calcIncludeDM, calcPerWeekOverride]);

  const applyCalcToForm = () => {
    if (!calcResult) return;
    setAmount(calcResult.total.toFixed(2));
    if (!description.trim()) {
      setDescription(calcResult.suggested_description);
    }
    toast.success(`Applied $${calcResult.total.toFixed(2)} to the invoice`);
  };

  const validate = () => {
    if (!parent) {
      toast.error("Please select a parent first");
      return null;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return null;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount > 0");
      return null;
    }
    const child = resolveChild();
    return {
      parent_id: parent.id,
      description: description.trim(),
      amount: amt,
      child_id: child.id,
      child_name: child.name,
      note: note.trim() || null,
      send_email: sendEmail,
      linked_registration_id: linkedRegistrationId || null,
    };
  };

  const openPreview = async () => {
    const body = validate();
    if (!body) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/manual-invoice`,
        { ...body, preview: true },
        { withCredentials: true }
      );
      setPreviewData(res.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to generate preview"); toast.error(__e.message, __e.opts); };
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    const body = validate();
    if (!body) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/manual-invoice`,
        body,
        { withCredentials: true }
      );
      setLastResult(res.data);
      setPreviewOpen(false);
      toast.success(
        sendEmail
          ? `Invoice #${res.data.invoice_reference} sent to ${res.data.parent_email}`
          : `Invoice #${res.data.invoice_reference} created`
      );
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to create invoice"); toast.error(__e.message, __e.opts); };
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdfSlip = () => {
    if (!lastResult?.invoice_reference) return;
    const url = `${API}/payments/admin/payment-slip-pdf/${encodeURIComponent(lastResult.invoice_reference)}`;
    window.open(url, "_blank", "noopener");
  };

  const copyEmailHtml = async () => {
    if (!lastResult?.email_html) return;
    try {
      await navigator.clipboard.writeText(lastResult.email_html);
      toast.success("Email HTML copied — paste into any mail client");
    } catch {
      toast.error("Clipboard access blocked by browser");
    }
  };

  const [markingPaid, setMarkingPaid] = useState(false);
  const [paidMethod, setPaidMethod] = useState("cash");
  const [bankRef, setBankRef] = useState("");

  const markAsPaid = async () => {
    if (!lastResult?.transaction_id) return;
    setMarkingPaid(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/manual-invoice/${lastResult.transaction_id}/mark-paid`,
        { payment_method: paidMethod, bank_reference: bankRef.trim() || null },
        { withCredentials: true }
      );
      setLastResult({ ...lastResult, marked_paid: true, payment_method: res.data.payment_method, paid_at: res.data.paid_at });
      toast.success(`Invoice #${lastResult.invoice_reference} marked as paid (${res.data.payment_method})`);
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to mark as paid"); toast.error(__e.message, __e.opts); };
    } finally {
      setMarkingPaid(false);
    }
  };

  const formatTs = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-6 md:p-10">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Back to Admin Panel
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <FileText className="w-10 h-10 text-emerald-400" />
            Create Manual Invoice
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Send a parent an ad-hoc invoice with a PayNow QR code — for forgotten registration fees,
            kit, tournament entries, or anything else not on their regular enrollment.
          </p>
        </div>

        {lastResult ? (
          /* Success confirmation */
          <Card className="p-6 bg-emerald-500/5 border border-emerald-500/40" data-testid="invoice-success-card">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white">
                  {lastResult.marked_paid ? "Invoice marked as PAID" : "Invoice created"}
                </h3>

                {/* BIG recipient banner */}
                {lastResult.email_sent ? (
                  <div className="mt-3 p-4 bg-emerald-900/30 border border-emerald-600/50 rounded-lg" data-testid="recipient-banner">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-emerald-300 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold text-emerald-200">
                          Invoice emailed to{" "}
                          <span className="font-mono text-white">{lastResult.parent_email}</span>
                        </p>
                        {lastResult.parent_name && (
                          <p className="text-sm text-emerald-300/80">({lastResult.parent_name})</p>
                        )}
                        {lastResult.bcc_email && (
                          <p className="text-xs text-emerald-300/70 mt-1">
                            Copy sent to <span className="font-mono">{lastResult.bcc_email}</span> (BCC)
                          </p>
                        )}
                        {lastResult.sent_at && (
                          <p className="text-xs text-emerald-300/70 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTs(lastResult.sent_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 p-4 bg-amber-900/20 border border-amber-600/40 rounded-lg">
                    <p className="text-amber-300 text-sm">
                      Saved without email. Use the download button below to send manually.
                    </p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wide">Reference</p>
                    <p className="font-mono text-emerald-300" data-testid="invoice-ref">{lastResult.invoice_reference}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wide">Amount</p>
                    <p className="font-bold text-white">${lastResult.amount.toFixed(2)}</p>
                  </div>
                </div>

                {/* Action buttons row */}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    onClick={downloadPdfSlip}
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid="download-pdf-slip-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF slip
                  </Button>
                  {lastResult.email_html && (
                    <Button
                      onClick={copyEmailHtml}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      data-testid="copy-email-html-btn"
                    >
                      <CopyIcon className="w-4 h-4 mr-2" />
                      Copy email HTML
                    </Button>
                  )}
                  <Link to={`/admin/payment-tracking?ref=${encodeURIComponent(lastResult.invoice_reference)}`}>
                    <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="view-in-tracking-btn">
                      <Eye className="w-4 h-4 mr-2" />
                      View in Payment Tracking
                    </Button>
                  </Link>
                </div>

                {lastResult.marked_paid && (
                  <p className="mt-3 text-sm text-emerald-300">
                    Paid via <span className="capitalize">{lastResult.payment_method?.replace("_", " ")}</span>
                  </p>
                )}

                {/* Mark-as-paid panel: only show if not yet paid */}
                {!lastResult.marked_paid && (
                  <div className="mt-5 p-4 bg-slate-900/60 border border-slate-700 rounded-lg" data-testid="mark-paid-panel">
                    <p className="text-sm font-semibold text-white mb-2">
                      Parent paid you already (cash, bank transfer, in-person)?
                    </p>
                    <p className="text-xs text-slate-400 mb-3">
                      Close out this invoice without waiting for Payment Verification.
                    </p>
                    <div className="grid sm:grid-cols-[1fr_1fr] gap-2 mb-3">
                      <select
                        value={paidMethod}
                        onChange={(e) => setPaidMethod(e.target.value)}
                        className="bg-slate-800 border border-slate-700 text-white rounded-md px-2 py-2 text-sm"
                        data-testid="paid-method-select"
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="paynow_manual">PayNow (already received)</option>
                        <option value="cheque">Cheque</option>
                        <option value="other">Other</option>
                      </select>
                      <Input
                        value={bankRef}
                        onChange={(e) => setBankRef(e.target.value)}
                        placeholder="Bank ref (optional)"
                        className="bg-slate-800 border-slate-700 text-white text-sm"
                        data-testid="paid-bank-ref-input"
                      />
                    </div>
                    <Button
                      onClick={markAsPaid}
                      disabled={markingPaid}
                      className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                      data-testid="mark-paid-btn"
                    >
                      {markingPaid ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Mark as paid now
                    </Button>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button onClick={() => { resetForm(); setLastResult(null); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-another-btn">
                    Create another
                  </Button>
                  <Link to="/admin">
                    <Button variant="outline" className="border-slate-600">Back to Admin Panel</Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-6 md:p-8 bg-slate-900/60 border border-slate-800">
            {/* Parent picker */}
            <div className="mb-6">
              <Label className="text-slate-200 mb-2 block">1. Pick a parent</Label>
              {parent ? (
                <div className="flex items-center justify-between bg-slate-800 border border-slate-700 rounded-lg p-3" data-testid="selected-parent">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="font-semibold text-white">{parent.name || `${parent.given_name || ""} ${parent.last_name || ""}`.trim() || parent.email}</p>
                      <p className="text-xs text-slate-400">{parent.email}{parent.phone ? ` · ${parent.phone}` : ""}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setParent(null); setQ(""); }} className="text-slate-400 hover:text-white" data-testid="clear-parent-btn">
                    Change
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Start typing name or email..."
                    className="pl-9 bg-slate-800 border-slate-700 text-white"
                    data-testid="parent-search-input"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
                  {results.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                      {results.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => { setParent(p); setQ(""); setResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-700 border-b border-slate-700/50 last:border-b-0"
                          data-testid={`parent-option-${p.id}`}
                        >
                          <p className="font-semibold text-white text-sm">{p.name || `${p.given_name || ""} ${p.last_name || ""}`.trim() || p.email}</p>
                          <p className="text-xs text-slate-400">{p.email}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <Label className="text-slate-200 mb-2 block">2. What is this charge for?</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Registration fee 2026 Term 1"
                className="bg-slate-800 border-slate-700 text-white mb-2"
                data-testid="description-input"
              />
              <div className="flex flex-wrap gap-1.5">
                {PRESET_DESCRIPTIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDescription(d)}
                    className="text-xs px-2 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600"
                    data-testid={`preset-${d.slice(0, 10)}`}
                  >
                    {d.split(" (")[0]}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount + Child selector */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-slate-200 mb-2 block">3. Amount (SGD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="100.00"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="amount-input"
                />
              </div>
              <div>
                <Label className="text-slate-200 mb-2 block">
                  Child {loadingChildren && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
                </Label>
                {!parent ? (
                  <div className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md text-slate-500 text-sm">
                    Pick a parent first
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedChildId}
                      onChange={(e) => setSelectedChildId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-2 py-2 text-sm"
                      data-testid="child-select"
                    >
                      <option value="__none__">— No specific child —</option>
                      {parentChildren.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.team_name ? ` · ${c.team_name}` : ""}
                        </option>
                      ))}
                      <option value="__manual__">Other / enter name manually…</option>
                    </select>
                    {selectedChildId === "__manual__" && (
                      <Input
                        value={manualChildName}
                        onChange={(e) => setManualChildName(e.target.value)}
                        placeholder="e.g. Andrew P"
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                        data-testid="child-manual-input"
                      />
                    )}
                    {parent && parentChildren.length === 0 && !loadingChildren && (
                      <p className="text-xs text-amber-400 mt-1">
                        This parent has no linked children yet. Choose "Other" to enter a name manually.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Optional registration linkage — top-up flow */}
            {parent && (
              <div className="mb-6">
                <Label className="text-slate-200 mb-2 block">
                  Link to existing registration (optional)
                  {loadingRegs && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
                </Label>
                <select
                  value={linkedRegistrationId}
                  onChange={(e) => setLinkedRegistrationId(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-2 py-2 text-sm"
                  data-testid="link-registration-select"
                >
                  <option value="">— Standalone manual invoice (don&apos;t link) —</option>
                  {eligibleRegs.map((r) => {
                    const tag = r.shortfall > 0.01
                      ? `short by $${r.shortfall.toFixed(2)}`
                      : (r.is_settled ? "settled" : "no shortfall");
                    const kids = (r.child_names || []).slice(0, 2).join(", ");
                    return (
                      <option key={r.registration_id} value={r.registration_id}>
                        {r.term_name || r.term_id || "Term?"} · {kids || "—"} · ${r.amount_invoiced.toFixed(2)} ({tag})
                      </option>
                    );
                  })}
                </select>
                {linkedRegistrationId && (() => {
                  const r = eligibleRegs.find((x) => x.registration_id === linkedRegistrationId);
                  if (!r) return null;
                  return (
                    <div className="mt-2 p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-200" data-testid="link-reg-summary">
                      <p className="font-medium">{r.payment_reference || r.registration_id}</p>
                      <p>Invoiced ${r.amount_invoiced.toFixed(2)} · Paid ${r.paid_so_far.toFixed(2)} · Shortfall ${r.shortfall.toFixed(2)}</p>
                      <p className="text-[11px] text-emerald-300/80 mt-1">Marking this manual invoice paid will auto-settle that registration if the totals add up.</p>
                    </div>
                  );
                })()}
                {!loadingRegs && eligibleRegs.length === 0 && (
                  <p className="text-xs text-slate-500 mt-1">No registrations on file for this parent yet.</p>
                )}
              </div>
            )}

            {/* Fee Calculator toggle */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowCalc((v) => !v)}
                disabled={!parent}
                className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-2 disabled:opacity-40"
                data-testid="toggle-calculator"
              >
                <Calculator className="w-4 h-4" />
                {showCalc ? "Hide" : "Open"} fee calculator
                {!parent && <span className="text-slate-500 text-xs">(select a parent first)</span>}
              </button>

              {showCalc && parent && (
                <Card className="mt-3 p-4 bg-slate-800/60 border border-emerald-500/30" data-testid="calc-panel">
                  <p className="text-xs text-slate-400 mb-3">
                    Auto-applies {parent.email}'s membership status ({[
                      parent.member_type === "section" && "Section",
                      parent.is_scc_member && "Club",
                      parent.is_digital_member && "Digital",
                    ].filter(Boolean).join(" + ") || "Non-member"}).
                  </p>

                  {/* Term + children */}
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-slate-400 mb-1 block">Term</Label>
                      <select
                        value={calcTermId}
                        onChange={(e) => setCalcTermId(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-2 text-sm"
                        data-testid="calc-term-select"
                      >
                        {terms.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400 mb-1 block">Children</Label>
                      <Input
                        type="number"
                        min="1"
                        value={calcChildren}
                        onChange={(e) => setCalcChildren(Math.max(1, parseInt(e.target.value || "1", 10)))}
                        className="bg-slate-900 border-slate-700 text-white h-9"
                        data-testid="calc-children-input"
                      />
                    </div>
                  </div>

                  {/* Line-item toggles */}
                  <div className="space-y-2 mb-3">
                    <label className="flex items-center gap-3 p-2 bg-slate-900/40 rounded-md cursor-pointer" data-testid="calc-reg-fee-row">
                      <Switch
                        checked={calcIncludeRegFee}
                        onCheckedChange={setCalcIncludeRegFee}
                        id="calc-reg-fee"
                      />
                      <span className="text-sm text-slate-300 flex-1">Include registration fee (× children)</span>
                    </label>

                    <div className="p-2 bg-slate-900/40 rounded-md" data-testid="calc-sessions-row">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-slate-300 flex-1">Sessions per week</span>
                        <Input
                          type="number"
                          min="0"
                          value={calcSessionsPerWeek}
                          onChange={(e) => setCalcSessionsPerWeek(Math.max(0, parseInt(e.target.value || "0", 10)))}
                          className="w-20 h-8 bg-slate-900 border-slate-700 text-white text-center"
                          data-testid="calc-sessions-input"
                        />
                      </div>
                      {calcSessionsPerWeek > 0 && (
                        <div className="flex items-center gap-2 ml-1">
                          <span className="text-xs text-slate-400">Per-week price override ($):</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={calcResult?.per_week_price?.toFixed(2) || "auto from rate table"}
                            value={calcPerWeekOverride}
                            onChange={(e) => setCalcPerWeekOverride(e.target.value)}
                            className="w-28 h-7 bg-slate-900 border-slate-700 text-white text-xs"
                            data-testid="calc-per-week-override"
                          />
                        </div>
                      )}
                    </div>

                    <label className="flex items-center gap-3 p-2 bg-slate-900/40 rounded-md cursor-pointer" data-testid="calc-dm-row">
                      <Switch
                        checked={calcIncludeDM}
                        onCheckedChange={setCalcIncludeDM}
                        id="calc-dm"
                        disabled={!!parent?.is_digital_member}
                      />
                      <span className="text-sm text-slate-300 flex-1">
                        Include Digital Membership
                        {parent?.is_digital_member && <span className="text-emerald-400 text-xs ml-1">(already active)</span>}
                      </span>
                    </label>
                  </div>

                  {/* Results */}
                  {calcLoading && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Calculating…
                    </div>
                  )}
                  {!calcLoading && calcResult && calcResult.line_items.length > 0 && (
                    <div className="bg-slate-900/80 border border-slate-700 rounded-md p-3 mt-2" data-testid="calc-result">
                      {calcResult.line_items.map((li, i) => (
                        <div key={i} className="flex justify-between text-sm text-slate-300 py-1">
                          <span className="flex-1">{li.label}</span>
                          <span className="font-mono text-white">${li.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between items-center">
                        <span className="font-semibold text-emerald-300">Total</span>
                        <span className="font-mono font-bold text-emerald-300 text-lg">${calcResult.total.toFixed(2)}</span>
                      </div>
                      <Button
                        onClick={applyCalcToForm}
                        size="sm"
                        className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="calc-apply-btn"
                      >
                        <Check className="w-4 h-4 mr-1" /> Use this amount & description
                      </Button>
                    </div>
                  )}
                </Card>
              )}
            </div>

            {/* Note */}
            <div className="mb-6">
              <Label className="text-slate-200 mb-2 block">Note to parent (optional — shown in email)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. You paid the term fee but the one-time registration fee was missed — please pay this separately."
                rows={3}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="note-input"
              />
            </div>

            {/* Send email toggle */}
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
              <Switch checked={sendEmail} onCheckedChange={setSendEmail} id="send-email-switch" data-testid="send-email-switch" />
              <label htmlFor="send-email-switch" className="text-sm text-slate-300 cursor-pointer flex-1">
                Email the invoice + PayNow QR to the parent immediately (uncheck to just save and send later)
              </label>
            </div>

            {/* BCC notice */}
            {sendEmail && (
              <div className="mb-6 px-3 py-2 bg-slate-800/40 border border-slate-700/60 rounded-md text-xs text-slate-400 flex items-center gap-2" data-testid="bcc-notice">
                <Mail className="w-3.5 h-3.5 text-slate-500" />
                <span>
                  A copy will be BCC'd to <span className="font-mono text-slate-300">{INVOICE_BCC}</span>
                </span>
              </div>
            )}

            {/* Preview + Submit */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={openPreview}
                disabled={submitting || !parent}
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800 flex-1 py-6 text-base"
                data-testid="preview-invoice-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                Preview invoice
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !parent}
                className="bg-emerald-600 hover:bg-emerald-700 flex-1 py-6 text-base"
                data-testid="create-invoice-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                {sendEmail ? "Create & email parent" : "Create (no email)"}
              </Button>
            </div>
          </Card>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-950 border-slate-800" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Preview invoice email</DialogTitle>
              <DialogDescription className="text-slate-400">
                This is exactly what the parent will receive. Review carefully before sending.
              </DialogDescription>
            </DialogHeader>

            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                <span className="ml-2 text-slate-400">Rendering preview…</span>
              </div>
            ) : previewData ? (
              <>
                <div className="space-y-2 text-sm mb-3">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-emerald-400" />
                    <span className="text-slate-500">To:</span>
                    <span className="font-mono text-white" data-testid="preview-recipient">{previewData.parent_email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <span className="text-slate-500 ml-6">BCC:</span>
                    <span className="font-mono">{previewData.bcc_email || INVOICE_BCC}</span>
                  </div>
                </div>

                <div className="flex-1 overflow-auto bg-white rounded-lg border border-slate-700" data-testid="preview-iframe-wrap">
                  <iframe
                    title="Invoice preview"
                    srcDoc={previewData.email_html}
                    style={{ width: "100%", height: "60vh", border: "none" }}
                  />
                </div>
              </>
            ) : null}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(false)}
                className="border-slate-600 text-slate-200"
                data-testid="preview-cancel-btn"
              >
                Back to edit
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !previewData}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="preview-send-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {sendEmail ? "Looks good — send now" : "Save (no email)"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}